import useRouter from "common/hooks/use-router";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import useItem from "hooks/useItem";
import type { ContactRequest } from "types";
import PageLoading from "common/components/PageLoading/PageLoading";
import Icon from "components/Icon/Icon";
import TypeBadge from "components/badges/TypeBadge";
import Avatar from "common/components/Avatar/Avatar";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import fetchJSON from "common/utils/fetchJSON";
import Button from "common/components/Button/Button";
import AlertsContext from "providers/AlertProvider";

const typeColors: Record<string, string> = {
  contact: "#6366f1",
  event: "#f59e0b",
  subscription: "#10b981",
};

const statusColors: Record<string, string> = {
  new: "#ef4444",
  processed: "#22c55e",
};

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-sm text-slate-700 whitespace-pre-line">
        {value}
      </span>
    </div>
  );
}

function ContactRequestDetail() {
  const router = useRouter();
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const id = router.query.id as string;
  const [isUpdating, setIsUpdating] = useState(false);

  const { item, isFetching, fetchItem } = useItem<ContactRequest>(
    "contact-requests",
    id,
    {}
  );

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      router.navigate(-1);
      return;
    }
    router.navigate("/contact-requests");
  }, [router]);

  const handleProcess = useCallback(async () => {
    setIsUpdating(true);
    try {
      await fetchJSON({
        url: `contact-requests/${id}/process`,
        method: "PATCH",
      });
      await fetchItem(id);
      setAlert(t("contact-requests.markedAsProcessed"), "success");
    } catch (e: any) {
      setAlert(e, "danger");
    } finally {
      setIsUpdating(false);
    }
  }, [id, fetchItem, setAlert, t]);

  const handleReopen = useCallback(async () => {
    setIsUpdating(true);
    try {
      await fetchJSON({
        url: `contact-requests/${id}/reopen`,
        method: "PATCH",
      });
      await fetchItem(id);
      setAlert(t("contact-requests.markedAsNew"), "success");
    } catch (e: any) {
      setAlert(e, "danger");
    } finally {
      setIsUpdating(false);
    }
  }, [id, fetchItem, setAlert, t]);

  if (isFetching && !item) return <PageLoading />;

  const request = item as ContactRequest;

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <div className="border rounded-lg border-slate-200 bg-white p-6 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t("actions.back")}
              className="flex justify-center items-center w-8 h-8 duration-200 ease-in-out relative right-0 hover:right-2"
            >
              <Icon name="ChevronLeftIcon" className="w-6 h-6" />
            </button>
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-primary">
                {request?.author?.username ?? "-"}
              </h1>
              <div className="flex items-center gap-2">
                {request?.type && (
                  <TypeBadge
                    label={t(`contact-requests.type-${request.type}`)}
                    color={typeColors[request.type]}
                  />
                )}
                {request?.status && (
                  <TypeBadge
                    label={t(`contact-requests.status-${request.status}`)}
                    color={statusColors[request.status]}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {request?.status === "new" && (
              <Button
                title={t("contact-requests.markAsProcessed")}
                icon="CheckIcon"
                type="primary"
                onClick={handleProcess}
                loading={isUpdating}
                disabled={isUpdating}
              />
            )}
            {request?.status === "processed" && (
              <Button
                title={t("contact-requests.markAsNew")}
                icon="ArrowLongUpIcon"
                type="warning-line"
                onClick={handleReopen}
                loading={isUpdating}
                disabled={isUpdating}
              />
            )}
          </div>
        </div>
      </div>

      <div className="white-box flex flex-col gap-6">
        <div className="flex flex-wrap gap-6">
          {request?.author && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                {t("contact-requests.author")}
              </span>
              <div className="flex items-center gap-2">
                <Avatar
                  name={request.author.username}
                  image={request.author.avatar}
                  size="8"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-slate-700">
                    {request.author.username}
                  </span>
                  {request.author.email && (
                    <span className="text-xs text-slate-400">
                      {request.author.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {request?.organization && (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                {t("contact-requests.organization")}
              </span>
              <div className="flex items-center gap-2">
                {request.organization.logo ? (
                  <img
                    src={ImageFromStrapiMedia(request.organization.logo).uri}
                    alt={request.organization.name}
                    className="max-h-6 max-w-[48px] object-contain"
                  />
                ) : null}
                <span className="text-sm font-semibold text-slate-700">
                  {request.organization.name}
                </span>
              </div>
            </div>
          )}

          <InfoRow
            label={t("contact-requests.createdAt")}
            value={
              request?.createdAt
                ? new Date(request.createdAt).toLocaleString("fr-FR")
                : null
            }
          />
        </div>

        {request?.type === "contact" && (
          <InfoRow
            label={t("contact-requests.content")}
            value={request.content}
          />
        )}

        {request?.type === "event" && (
          <>
            {(request.startDateTime || request.endDateTime) && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  {t("contact-requests.eventDate")}
                </span>
                <span className="text-sm text-slate-700">
                  {request.startDateTime &&
                    new Date(request.startDateTime).toLocaleString("fr-FR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  {request.startDateTime && request.endDateTime && " → "}
                  {request.endDateTime &&
                    new Date(request.endDateTime).toLocaleString("fr-FR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                </span>
              </div>
            )}
            <InfoRow
              label={t("contact-requests.content")}
              value={request.content}
            />
          </>
        )}

        {request?.type === "subscription" && (
          <>
            {request.currentSubscription && (
              <InfoRow
                label={t("contact-requests.currentSubscription")}
                value={request.currentSubscription.name}
              />
            )}
            {request.newSubscription && (
              <InfoRow
                label={t("contact-requests.newSubscription")}
                value={request.newSubscription.name}
              />
            )}
            <InfoRow
              label={t("contact-requests.content")}
              value={request.content}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default ContactRequestDetail;
